/**
 * VueCuisinePage — effectif cantine du jour pour la cuisine (repas à préparer).
 * Le périscolaire et l'ALSH ont leurs propres onglets. Accessible manager + agent.
 *
 * ─── CORRECTION DU 27/07 : LES ADULTES MANQUAIENT ───────────────────────────
 *
 * Cet écran lisait `getEffectifCantine`, qui ne compte que les réservations
 * ENFANTS. Un agent inscrit au repas (mig 250) donnait « 0 repas à préparer »
 * alors que les Effectifs du jour affichaient « 1 attendu, 1 présent ». La
 * cuisine aurait cuisiné zéro repas.
 *
 * On lit désormais la liste du jour complète, et on affiche les deux blocs
 * SÉPARÉMENT — la cuisine doit lire « 12 enfants + 3 adultes », jamais « 15 » :
 * les portions ne sont pas les mêmes.
 *
 * ─── ATTENDUS vs POINTÉS ────────────────────────────────────────────────────
 *
 * Le chiffre qui commande la production est celui des ATTENDUS : cet écran se
 * consulte le matin, avant le service, quand personne n'est encore pointé.
 * Le pointé est affiché à côté, pour le contrôle après le repas.
 */

import { useEffect, useState } from 'react';
import { UtensilsCrossed, UserRound } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { Card, CardBody, Input, Field, Alert } from '@/components/ui';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';

interface Bloc { attendus: number; pointes: number; }
const VIDE: Bloc = { attendus: 0, pointes: 0 };

export function VueCuisinePage() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [enfants, setEnfants] = useState<Bloc>(VIDE);
  const [adultes, setAdultes] = useState<Bloc>(VIDE);
  const [absents, setAbsents] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    (async () => {
      try {
        const liste = await api.scolaire.getListeCantine(date);
        if (cancelled) return;
        const attendu = (s: string) => s === 'reserve' || s === 'present';
        const lignes = liste.lignes ?? [];
        const ad = liste.adultes ?? [];
        setEnfants({
          attendus: lignes.filter((l) => attendu(l.statut)).length,
          pointes: lignes.filter((l) => l.statut === 'present').length,
        });
        setAdultes({
          attendus: ad.filter((a) => attendu(a.statut)).length,
          pointes: ad.filter((a) => a.statut === 'present').length,
        });
        setAbsents(lignes.filter((l) => l.statut === 'absent' || l.statut === 'absent_justifie').length);
      } catch (e) {
        if (!cancelled) setError(e instanceof ApiException ? e.message : 'Erreur de chargement');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [date]);

  const total = enfants.attendus + adultes.attendus;

  return (
    <ScolaireLayout activeTab="cantine">
      <main className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <div className="mb-6">
          <h1 className="font-display text-2xl font-semibold tracking-r-tight text-ink">Vue cuisine — cantine</h1>
          <p className="mt-1 text-sm text-ink-muted">
            Nombre de repas à préparer pour la journée. (Le périscolaire et l'ALSH ont leurs propres onglets.)
          </p>
        </div>

        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        <div className="mb-6 max-w-xs">
          <Field label="Date" htmlFor="date-cuisine">
            <Input id="date-cuisine" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </div>

        <Card className="mb-4">
          <CardBody>
            <div className="text-xs uppercase tracking-wide text-ink-subtle">Total repas à préparer</div>
            <div className="text-5xl font-semibold tabular-nums text-ink">{loading ? '—' : total}</div>
            <p className="mt-1 text-sm text-ink-muted">
              enfants et adultes confondus
              {!loading && absents > 0 ? ` · ${absents} absence${absents > 1 ? 's' : ''} déclarée${absents > 1 ? 's' : ''}` : ''}
            </p>
          </CardBody>
        </Card>

        <div className="grid gap-4 sm:grid-cols-2">
          <Card>
            <CardBody>
              <div className="mb-3 flex items-center gap-2">
                <UtensilsCrossed className="h-5 w-5 text-cantine-fg" aria-hidden="true" />
                <h2 className="font-display text-base font-semibold text-ink">Enfants</h2>
              </div>
              <div className="text-4xl font-semibold tabular-nums text-ink">{loading ? '—' : enfants.attendus}</div>
              <p className="mt-1 text-sm text-ink-muted">
                attendus{!loading ? ` · ${enfants.pointes} pointé${enfants.pointes > 1 ? 's' : ''}` : ''}
              </p>
            </CardBody>
          </Card>

          {/* Affiché MÊME à zéro : c'est le cas exact remonté par la mairie —
              un agent inscrit, aucun enfant, et l'écran annonçait 0. */}
          <Card>
            <CardBody>
              <div className="mb-3 flex items-center gap-2">
                <UserRound className="h-5 w-5 text-cantine-fg" aria-hidden="true" />
                <h2 className="font-display text-base font-semibold text-ink">Adultes</h2>
              </div>
              <div className="text-4xl font-semibold tabular-nums text-ink">{loading ? '—' : adultes.attendus}</div>
              <p className="mt-1 text-sm text-ink-muted">
                attendus{!loading ? ` · ${adultes.pointes} pointé${adultes.pointes > 1 ? 's' : ''}` : ''}
              </p>
            </CardBody>
          </Card>
        </div>
      </main>
    </ScolaireLayout>
  );
}
